import axios from "axios";
import { store } from "@/app_redux/stores/root_store";
import { setClarityTag, upgradeClaritySession } from "@/app/utils/clarity";
import { ORG_APIS } from "./api_config";
import { getDiagnosticIdentity } from "./tokenDiagnostics";

export type LogoutTrigger =
  | "AXIOS_REFRESH_FAILED" // 401/403 -> token refresh attempted -> refresh failed -> forced logout
  | "INACTIVITY_TIMEOUT" // 2h no activity -> auto logout
  | "CROSS_TAB_FORCED_LOGOUT"
  | "AXIOS_REFRESH_FAILED_REDIRECT_TO_LOGIN" // doLocalLogout actually cleared storage + redirected
  // The silent exit: the root /app route loader found no access_token and
  // redirected to login. This logged nothing before, which is why mid-EMR
  // logouts never appeared in click-logs even though the pipeline was healthy.
  | "ROUTE_GUARD_NO_ACCESS_TOKEN";

// Dedicated client so this log call does NOT pass through the global axios
// response interceptor in setupAxios.ts (avoids any chance of recursion).
const diagClient = axios.create();

export const logLogoutDiagnostic = (trigger: LogoutTrigger, detail?: Record<string, unknown>) => {
  try {
    const state = store.getState();
    const email = state?.auth?.user?.email;

    const identity = getDiagnosticIdentity();

    // Microsoft Clarity: mirror the trigger + page onto the session so these
    // recordings are filterable, and force-retain the session (Clarity samples
    // and drops sessions otherwise) so the mid-EMR logout recording survives.
    // Runs before the fetch — and before doLocalLogout wipes storage/redirects —
    // so the recording is upgraded while the doctor is still on the EMR page.
    // No-ops safely when Clarity is disabled or not yet loaded.
    setClarityTag("logout_trigger", trigger);
    setClarityTag("logout_page", window?.location?.pathname ?? "");
    upgradeClaritySession(`logout_${trigger}`);

    const body = {
      event_type: "VMS_LOGOUT_DIAGNOSTIC",
      event_action: trigger,
      // Where the logout happened — proves whether it was the EMR page.
      scr_id: window?.location?.pathname,
      source: window?.location?.href,
      // Identity comes from the in-memory cache first. Reading it straight
      // from localStorage meant that if the logout was CAUSED by storage
      // being wiped, this report arrived with no clinic and no user — the one
      // row that mattered was unfilterable.
      clinic_id: identity.clinic_id,
      clinic_name: identity.clinic_name,
      vms_user_id: email ?? identity.vms_user_id,
      // Structured extra context (failed url, status, refresh error, etc.).
      filter_values: detail,
      timestamp: Date.now(),
    };

    const url = ORG_APIS?.CLICK_LOG_API?.baseURL ?? "";

    // Transport note — this used to use navigator.sendBeacon, and that is the
    // most likely reason no logout diagnostic has ever been received despite
    // the endpoint being healthy. sendBeacon returns true for "queued", NOT
    // "delivered", so the old code returned early and never tried the axios
    // fallback. A beacon that is dropped is indistinguishable from one that
    // worked. Meanwhile useClickLogAPI posts to this exact endpoint over
    // plain axios and those rows do arrive.
    //
    // fetch + keepalive gives us both halves: a full, observable CORS request
    // (unlike a beacon) that still survives the page navigating away (unlike
    // a bare XHR), which is what a logout does immediately afterwards.
    if (typeof fetch === "function") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {
        /* swallow — logging is best-effort */
      });
      return;
    }

    // Fire-and-forget fallback: diagnostics must never block or break logout.
    diagClient
      .post(url, body, {
        headers: { "Content-Type": "application/json" },
      })
      .catch(() => {
        /* swallow — logging is best-effort */
      });
  } catch {
    /* never throw from diagnostics */
  }
};
